/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';

import AlertsService from 'cio/services/alerts';
import AccountService from 'cio/services/account';
import AnalyticsService from 'cio/services/analytics';
import AuthService from 'cio/services/auth';
import EnvironmentModel from 'cio/models/environment';
import { IdentifierType } from 'cio/utils/identifiers';
import CampaignModel from 'cio/models/campaign';
import NewsletterModel from 'cio/models/newsletter';
import TemplateModel from 'cio/models/template';
import ActionModel from 'cio/models/action';
import TransactionalMessageModel from 'cio/models/transactional-message';

interface LanguageAttributeModalArgs {
  environment: EnvironmentModel;
  onClose: () => unknown;
  model?: NewsletterModel | CampaignModel | TransactionalMessageModel;
  template?: TemplateModel;
  action?: ActionModel;
  disabled?: boolean;
  hasReadOnlyPermission: boolean;
}

export default class LanguageAttributeModal extends Component<LanguageAttributeModalArgs> {
  @service alerts!: AlertsService;
  @service account!: AccountService;
  @service analytics!: AnalyticsService;
  @service auth!: AuthService;

  @tracked isSetupComplete = false;
  @tracked isShowingLanguageAttributeFeedbackForm = false;

  addLanguageExampleImage = new URL('cio/assets/images/add-language-example-x2.gif', import.meta.url).toString();
  peopleTalkingImage = new URL('cio/assets/images/illustrations/people-talking.png', import.meta.url).toString();

  get disabled() {
    return this.args.disabled ?? false;
  }

  get isSaveDisabled() {
    return (
      this.disabled ||
      this.args.hasReadOnlyPermission ||
      !this.args.environment.hasDirtyAttributes ||
      this.updateEnvironmentTask.isRunning
    );
  }

  get analyticsData() {
    let source = 'Settings - Language Attribute';

    if (this.args.model != null) {
      if (this.args.action == null && this.args.template == null) {
        source = 'VWB - Language Attribute';
      } else {
        source = 'Composer - Language Attribute';
      }
    }

    return {
      model: this.args.model,
      template: this.args.template,
      action: this.args.action,
      source,
    };
  }

  updateEnvironmentTask = task(async () => {
    let { environment } = this.args;

    try {
      if (environment.identifierType !== IdentifierType.MultiIdentifier) {
        (environment as any).identifiers = null;
      }

      await environment.save();

      this.isSetupComplete = true;
    } catch {
      this.args.onClose();
      this.cancelLanguageSetup();
      this.alerts.error(
        'environment.updated.error',
        'Something went wrong with setting up your language attribute. Please try again or contact support.',
      );
    }
  });

  @action
  openFeedbackForm() {
    this.analytics.track('languages', 'different_format_clicked', this.analyticsData);
    this.isShowingLanguageAttributeFeedbackForm = true;
  }

  @action
  closeModal() {
    if (!this.isSetupComplete) {
      this.cancelLanguageSetup();
    }

    this.args.onClose();
  }

  cancelLanguageSetup() {
    this.args.environment.rollbackAttributes();
    this.analytics.track('languages', 'attribute_setup_canceled', this.analyticsData);
  }

  @action
  setLanguageAttribute({ value }: { value: string }) {
    this.args.environment.languageAttribute = value;
  }
}
